/****************************************************************************
 * Copyright 2021 EPAM Systems
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *    http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 ***************************************************************************/
import {
  PresetMonomerRow,
  PresetMonomerLabel,
  PresetMonomerName,
  PresetIcon,
  PresetContainer,
  PresetName,
} from './PresetPreview.styles';
import styled from '@emotion/styled';
import { selectShowPreview } from 'state/common';
import { IconName } from 'ketcher-react';
import { KetMonomerClass } from 'ketcher-core';
import useIDTAliasesTextForPreset from '../../hooks/useIDTAliasesTextForPreset';
import MonomerPreviewProperties from '../MonomerPreviewProperties/MonomerPreviewProperties';
import { useAppSelector } from 'hooks';
import { PresetPreviewState } from 'state';

const icons: Extract<IconName, 'sugar' | 'base' | 'phosphate' | 'chem'>[] = [
  'sugar',
  'base',
  'phosphate',
];

interface Props {
  className?: string;
}

const PresetPreview = ({ className }: Props) => {
  const preview = useAppSelector(selectShowPreview) as PresetPreviewState;

  const { monomers, name, position, idtAliases } = preview;

  // Check if this is a CHEM chain (all monomers are CHEMs)
  const isChemChain = monomers.every(
    (monomer) => monomer?.props.MonomerClass === KetMonomerClass.CHEM,
  );

  const [, baseMonomer] = monomers;
  const presetName = name ?? baseMonomer?.props.Name;
  const axoLabsText = baseMonomer?.props.aliasAxoLabs;

  const idtAliasesText = useIDTAliasesTextForPreset({
    presetName,
    position,
    idtAliases,
  });

  const isMonomerPreviewPropertiesVisible = idtAliasesText || axoLabsText;

  return (
    <PresetContainer
      className={className}
      style={{ alignItems: 'flex-start' }}
      data-testid="polymer-library-preview"
    >
      <PresetName data-testid="preview-tooltip-title">{presetName}</PresetName>
      {monomers.map((monomer, index) =>
        monomer ? (
          <PresetMonomerRow key={monomer.props.id}>
            <PresetIcon name={isChemChain ? 'chem' : icons[index]} />
            <PresetMonomerLabel>{monomer.label}</PresetMonomerLabel>
            <PresetMonomerName>({monomer.props.Name})</PresetMonomerName>
          </PresetMonomerRow>
        ) : null,
      )}
      {isMonomerPreviewPropertiesVisible && (
        <MonomerPreviewProperties
          preset
          idtAliasesText={idtAliasesText ?? undefined}
          axoLabsText={axoLabsText}
        />
      )}
    </PresetContainer>
  );
};

const PresetStyledPreview = styled(PresetPreview)``;

export default PresetStyledPreview;
